import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { MobileDatePicker as DatePicker } from '@mui/x-date-pickers/MobileDatePicker';
import axios from 'axios';
import SearchIcon from '@mui/icons-material/Search';
import GetAppIcon from '@mui/icons-material/GetApp';
import moment from 'moment-timezone';
import {
    Grid,
    FormControl,
    InputLabel,
    MenuItem,
    Button as MaterialButton,
    Paper,
    Select,
    Menu,
    Container,
    CircularProgress,
} from '@mui/material';
import { styled as muiStyled } from '@mui/material/styles';
import UserLayout from '../UserLayout';
import useUser from '../../hooks/useUser';
import withAuth from '../../hoc/withAuth';
import SEO from '../common/SEO';
import { ROW_PER_PAGE_OPTIONS } from '../../config/pagination';
import { getErrorMessage } from '../../customer-call-sdk/lib/API';
import {
    SITE_DESCRIPTION,
    USAGE_PAGE_DESCRIPTION_PREFIX,
    USAGE_PAGE_TITLE,
} from '../../config/seo.constants';
import {
    TableContainer,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    TablePagination,
} from '../common/Table';
import PageTitle from '../common/PageTitle';
import {
    getBillingCodesByEmail,
    getBusinessUsageReport,
    GetBusinessUsageReportPayload,
} from '../../services/user-report.service';
import { B2BUsageReport } from '../../types/B2BUsageReport.type';
import { dateFormat } from '../../config/common.constants';
import { useToast } from '../../context/ToastContext';
import { useCall } from '../../customer-call-sdk';
import END_POINT from '../../config/endpoints.constants';

const StyledUsageReport = styled(UserLayout)``;

const StyledButton = muiStyled(MaterialButton)({
    background: 'green',
    borderRadius: 3,
    border: 0,
    color: 'white',
    height: 30,
    marginTop: '15px',
    width: '2px',
    textTransform: 'capitalize',
    '&:hover': {
        backgroundColor: 'green',
    },
});

const MuiDatePickerToolbar = {
    sx: {
        background: '#0a5',
        color: 'white',
        '& .MuiPickersToolbar-title': {
            color: 'white',
        },
    },
};

const NoUsageReport = () => (
    <div id="customer-usage-no-credit-logs-div" className="flex p-4 justify-center">
        No Usage Report
    </div>
);

const B2BUsageReports = () => {
    const toast = useToast();
    const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
    const [billingCodeList, setBillingCodeList] = useState<string[]>([]);
    const [B2BUsageReportList, setB2BUsageReportList] = useState<B2BUsageReport[]>([]);
    const [totalCount, setTotalCount] = useState(0);
    const [selectBillingCode, setSelectBillingCode] = useState<string>('');
    const [startDate, setStartDate] = useState(moment().startOf('day').toDate());
    const [endDate, setEndDate] = useState(moment().endOf('day').toDate());
    const maxDate = new Date();
    const isMenuOpen = Boolean(anchorEl);
    const [enableDownloadBtn, setEnableDownloadBtn] = useState<Boolean>(false);
    const [enableCircularProgress, setEnableCircularProgress] = useState<Boolean>(false);
    const [isExporting, setIsExporting] = useState(false);
    const isExportingRef = useRef(false);
    const [isFirstTimeLoad, setIsfirstTimeLoad] = useState<Boolean>(false);
    const [pagination, setPagination] = useState({
        page: 0,
        rowsPerPage: 10,
        offset: 0,
    });
    const user = useUser();
    const call = useCall();
    const { refreshedAccessToken } = call;
    const searchByBillingCodeRef = useRef<any>('');
    const handleMenuClose = () => {
        setAnchorEl(null);
    };

    const searchB2BUsageReportList = async () => {
        const payload: GetBusinessUsageReportPayload = {
            offset: pagination.offset,
            limit: pagination.rowsPerPage,
            fromDate: moment(startDate).toISOString(),
            toDate: moment(endDate).toISOString(),
            billingCode: selectBillingCode,
        };

        const { data, error } = await getBusinessUsageReport(payload);
        if (B2BUsageReportList && B2BUsageReportList.length > 0) {
            setB2BUsageReportList([]);
        }
        if (data) {
            setB2BUsageReportList(data.objects);
            setTotalCount(data.totalCount);
            setEnableCircularProgress(false);
        }
        if (error) {
            toast.error(error);
        }
    };

    const fetchAllBillingCodes = async (userEmail: string) => {
        const email = userEmail;
        const { data, error } = await getBillingCodesByEmail(email);
        if (data) {
            setBillingCodeList(data);
        }
        if (error) {
            toast.error(error);
        }
        setIsfirstTimeLoad(true);
    };

    useEffect(() => {
        setBillingCodeList([]);
        setB2BUsageReportList([]);
        setSelectBillingCode('');
        if (user && refreshedAccessToken) {
            fetchAllBillingCodes(user.email);
        }
    }, [user, refreshedAccessToken]);

    useEffect(() => {
        if (selectBillingCode) {
            setEnableCircularProgress(true);
            searchB2BUsageReportList();
        }
        if (!selectBillingCode && isFirstTimeLoad) {
            toast.error('Please select a billing code before searching.');
        }
    }, [pagination]);

    useEffect(() => {
        if (B2BUsageReportList.length === 0) {
            setEnableDownloadBtn(false);
        } else {
            setEnableDownloadBtn(true);
        }
    }, [B2BUsageReportList]);

    const handleBillingCodeSelection = (billingCode: any) => {
        setSelectBillingCode(billingCode.target.value);
    };

    function saveByteArray(reportName: string, type: string, byte: any) {
        const blob = new Blob([byte]);
        const link = document.createElement('a');
        link.href = window.URL.createObjectURL(blob);
        const fileName = `${reportName}.${type}`;
        link.download = fileName;
        link.click();
    }

    const exportCreditLogs = (type: string) => {
        if (isExportingRef.current) return;
        isExportingRef.current = true;
        handleMenuClose();
        setIsExporting(true);
        const reqData: any = {
            fromDate: moment(startDate).toISOString(),
            toDate: moment(endDate).toISOString(),
            billingCode: searchByBillingCodeRef.current.value
                ? searchByBillingCodeRef.current.value
                : selectBillingCode,
        };

        if (type === 'pdf') {
            axios
                .post(END_POINT.GENERATE_USAGE_REPORT_PDF, reqData, { responseType: 'arraybuffer' })
                .then((res) => {
                    saveByteArray('Usage_Report', type, res.data);
                })
                .catch((err) => {
                    toast.error(getErrorMessage(err));
                })
                .finally(() => {
                    isExportingRef.current = false;
                    setIsExporting(false);
                });
        }
        if (type === 'csv') {
            axios
                .post(END_POINT.GENERATE_USAGE_REPORT_CVS, reqData, {
                    responseType: 'arraybuffer',
                })
                .then((res) => {
                    saveByteArray('Usage_Report', type, res.data);
                })
                .catch((err) => {
                    toast.error(getErrorMessage(err));
                })
                .finally(() => {
                    isExportingRef.current = false;
                    setIsExporting(false);
                });
        }
    };

    const renderMenu = (
        <Menu
            anchorEl={anchorEl}
            anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
            keepMounted
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            open={isMenuOpen}
            onClose={handleMenuClose}
        >
            <MenuItem onClick={() => exportCreditLogs('csv')} aria-label="Export as CSV">
                CSV
            </MenuItem>
            <MenuItem onClick={() => exportCreditLogs('pdf')} aria-label="Export as PDF">
                PDF
            </MenuItem>
        </Menu>
    );

    const initialSearchB2BUsageReport = () => {
        setPagination({ ...pagination, page: 0, rowsPerPage: 10, offset: 0 });
    };

    const handleProfileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleChangePage = (event: any, newPage: any) => {
        const { page } = pagination;

        if (page < newPage) {
            setPagination({ ...pagination, offset: pagination.offset + 1, page: newPage });
        } else {
            setPagination({ ...pagination, offset: pagination.offset - 1, page: newPage });
        }
    };

    const handleChangeRowsPerPage = (event: any) => {
        setPagination((state) => ({
            ...state,
            rowsPerPage: event.target.value,
            page: 0,
            offset: 0,
        }));
    };

    return (
        <StyledUsageReport>
            <SEO
                title={USAGE_PAGE_TITLE}
                description={`${USAGE_PAGE_DESCRIPTION_PREFIX} - ${SITE_DESCRIPTION}`}
            />
            <Container>
                <PageTitle aria-label="Usage Report">Usage Report</PageTitle>
                <Paper className="p-4" elevation={2}>
                    <Grid container justifyContent="center" spacing={3}>
                        <Grid
                            size={{ xs: 12, sm: 6, md: 4 }}
                            style={{ display: 'flex', justifyContent: 'center' }}
                        >
                            {billingCodeList && billingCodeList.length ? (
                                <FormControl variant="standard" fullWidth>
                                    <InputLabel id="billing-code-label">Billing Code</InputLabel>

                                    <Select
                                        labelId="billing-code-dropdown-label"
                                        id="billing-code-select-standard"
                                        onChange={handleBillingCodeSelection}
                                        label="BillingCode"
                                        fullWidth
                                    >
                                        {billingCodeList.map((code: any) => (
                                            <MenuItem key={code ? code.length : ''} value={code}>
                                                {code}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            ) : (
                                <div>No Billing Codes</div>
                            )}
                        </Grid>
                        <Grid
                            size={{ xs: 12, sm: 6, md: 3 }}
                            style={{ display: 'flex', justifyContent: 'center' }}
                        >
                            <DatePicker
                                value={startDate as any}
                                format={dateFormat}
                                label="Start Date"
                                maxDate={maxDate as any}
                                onChange={(date: any) => setStartDate(date)}
                                slotProps={{
                                    toolbar: MuiDatePickerToolbar,
                                    textField: {
                                        fullWidth: true,
                                        variant: 'standard',
                                        'aria-label': 'Start Date',
                                    } as any,
                                }}
                            />
                        </Grid>
                        <Grid
                            size={{ xs: 12, sm: 6, md: 3 }}
                            style={{ display: 'flex', justifyContent: 'center' }}
                        >
                            <DatePicker
                                value={endDate as any}
                                label="End Date"
                                format={dateFormat}
                                minDate={moment(startDate) as any}
                                maxDate={maxDate as any}
                                onChange={(date: any) => setEndDate(date)}
                                slotProps={{
                                    toolbar: MuiDatePickerToolbar,
                                    textField: {
                                        fullWidth: true,
                                        variant: 'standard',
                                        'aria-label': 'End Date',
                                    } as any,
                                }}
                            />
                        </Grid>
                        <Grid
                            size={{ xs: 12, sm: 6, md: 2 }}
                            style={{ display: 'flex', justifyContent: 'center' }}
                        >
                            {enableCircularProgress ? (
                                <div>
                                    <CircularProgress color="primary" />
                                </div>
                            ) : (
                                <>
                                    <StyledButton
                                        className="mr-2"
                                        onClick={initialSearchB2BUsageReport}
                                        aria-label="Search on Usage Report"
                                    >
                                        <SearchIcon />
                                    </StyledButton>
                                    <StyledButton
                                        disabled={!enableDownloadBtn || isExporting}
                                        onClick={handleProfileMenuOpen}
                                        aria-label="GetAppIcon"
                                    >
                                        {isExporting ? (
                                            <CircularProgress size={20} />
                                        ) : (
                                            <GetAppIcon />
                                        )}
                                    </StyledButton>
                                </>
                            )}
                        </Grid>
                        {renderMenu}
                    </Grid>
                </Paper>
                <Paper className="mt-5" elevation={2}>
                    {B2BUsageReportList && B2BUsageReportList.length > 0 ? (
                        <>
                            <TableContainer>
                                <Table aria-label="simple table" size="medium">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Name</TableCell>
                                            <TableCell>Owned By</TableCell>
                                            <TableCell>Number Type</TableCell>
                                            <TableCell>Billing Code</TableCell>
                                            <TableCell>Phone Number</TableCell>
                                            <TableCell>Consolidated usage</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {B2BUsageReportList.map(
                                            (
                                                b2BUsageReport: B2BUsageReport,
                                                index: number,
                                                arr: any[],
                                            ) => (
                                                <TableRow key={arr ? arr.length : 0}>
                                                    <TableCell component="th" scope="row">
                                                        {b2BUsageReport.name}
                                                    </TableCell>
                                                    <TableCell>
                                                        {b2BUsageReport.ownedByType}
                                                    </TableCell>
                                                    <TableCell>
                                                        {b2BUsageReport.numberType}
                                                    </TableCell>
                                                    <TableCell>
                                                        {b2BUsageReport.billingCode}
                                                    </TableCell>
                                                    <TableCell>
                                                        {b2BUsageReport.phoneNumber}
                                                    </TableCell>
                                                    <TableCell>
                                                        {b2BUsageReport.totalUsage}
                                                    </TableCell>
                                                </TableRow>
                                            ),
                                        )}
                                    </TableBody>
                                </Table>
                                <TablePagination
                                    rowsPerPageOptions={ROW_PER_PAGE_OPTIONS}
                                    component="div"
                                    count={totalCount}
                                    rowsPerPage={pagination.rowsPerPage}
                                    page={pagination.page}
                                    onPageChange={handleChangePage}
                                    onRowsPerPageChange={handleChangeRowsPerPage}
                                />
                            </TableContainer>
                        </>
                    ) : (
                        <NoUsageReport />
                    )}
                </Paper>
            </Container>
        </StyledUsageReport>
    );
};

export default withAuth(B2BUsageReports);
